import { useEffect, useState } from "react";
import { X, Trash2, ArrowRightCircle, Archive } from "lucide-react";
import { TARGET_TIERS, TARGET_STATUS, tierFarge } from "./eventosUtils";

const Seksjon = ({ label, children }) => (
  <div className="mt-6">
    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400 mb-2">{label}</p>
    {children}
  </div>
);

const Felt = ({ label, verdi, onLagre, testid, textarea, type }) => {
  const [v, setV] = useState(verdi ?? "");
  useEffect(() => { setV(verdi ?? ""); }, [verdi]);
  const Tag = textarea ? "textarea" : "input";
  return (
    <label className="adm-label">
      {label}
      <Tag
        value={v}
        type={type || "text"}
        rows={textarea ? 3 : undefined}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => String(v) !== String(verdi ?? "") && onLagre(v)}
        className="adm-input"
        data-testid={testid}
      />
    </label>
  );
};

export default function TargetDrawer({ target, members, onPatch, onConvert, onDelete, onLukk }) {
  const [startAapen, setStartAapen] = useState(false);
  const [startAnsvarlig, setStartAnsvarlig] = useState(target.ownerId || "");
  const [startFolgOpp, setStartFolgOpp] = useState("");
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") (startAapen ? setStartAapen(false) : onLukk()); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onLukk, startAapen]);

  const konvertert = target.status === "converted";
  const aktivitet = [...(target.activity || [])].reverse();

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="target-drawer">
      <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={onLukk} />
      <aside className="sak-drawer relative w-full max-w-[560px] h-full bg-white shadow-2xl border-l border-[#E7E4DB] flex flex-col">
        <div className="px-6 pt-5 pb-4 border-b border-neutral-100 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <span
                className="inline-flex items-center justify-center w-7 h-7 rounded-md text-[13px] font-bold text-white mt-0.5"
                style={{ background: tierFarge(target.tier) }}
              >
                {target.tier}
              </span>
              <div>
                <h2 className="text-[19px] font-semibold leading-tight" data-testid="target-drawer-navn">{target.name}</h2>
                <p className="text-[12px] text-neutral-400 mt-0.5">
                  {[target.title, target.org].filter(Boolean).join(" · ") || "—"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button type="button" onClick={onDelete} className="p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" data-testid="target-slett-btn">
                <Trash2 size={15} />
              </button>
              <button type="button" onClick={onLukk} className="p-1.5 text-neutral-400 hover:text-neutral-800 hover:bg-neutral-100 rounded-md transition-colors" data-testid="target-drawer-lukk">
                <X size={17} />
              </button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <select
              value={target.tier}
              onChange={(e) => onPatch({ tier: e.target.value })}
              className="text-[12.5px] font-medium border border-[#DED9CD] rounded-lg px-2.5 py-1.5 bg-white focus:outline-none"
              data-testid="target-tier-select"
              disabled={konvertert}
            >
              {TARGET_TIERS.map((t) => (
                <option key={t.k} value={t.k}>{t.label}</option>
              ))}
            </select>
            <select
              value={target.status}
              onChange={(e) => onPatch({ status: e.target.value })}
              className="text-[12.5px] font-medium border border-[#DED9CD] rounded-lg px-2.5 py-1.5 bg-white focus:outline-none"
              data-testid="target-status-select"
              disabled={konvertert}
            >
              {TARGET_STATUS.filter((s) => s.k !== "converted" || konvertert).map((s) => (
                <option key={s.k} value={s.k}>{s.label}</option>
              ))}
            </select>
            {konvertert ? (
              <span className="text-[11.5px] font-medium text-emerald-600 ml-auto" data-testid="target-konvertert-tag">
                Konvertert til pipelinen
              </span>
            ) : (
              <div className="relative ml-auto">
                <button type="button" onClick={() => setStartAapen((v) => !v)} className="adm-btn" data-testid="target-konverter-btn">
                  <ArrowRightCircle size={13} /> Start outreach
                </button>
                {startAapen && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setStartAapen(false)} />
                    <div className="sak-modal absolute right-0 top-full mt-1.5 z-40 w-[260px] bg-white border border-[#E7E4DB] rounded-xl shadow-xl p-3.5" data-testid="target-start-meny">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-400 mb-2.5">Inn i pipelinen</p>
                      <label className="adm-label">
                        Ansvarlig
                        <select
                          value={startAnsvarlig}
                          onChange={(e) => setStartAnsvarlig(e.target.value)}
                          className="adm-input"
                          data-testid="target-start-ansvarlig"
                        >
                          <option value="">Ingen</option>
                          {(members || []).map((m) => (
                            <option key={m.id} value={m.id}>{m.name}</option>
                          ))}
                        </select>
                      </label>
                      <label className="adm-label mt-2.5">
                        Følg opp (valgfritt)
                        <input
                          type="date"
                          value={startFolgOpp}
                          onChange={(e) => setStartFolgOpp(e.target.value)}
                          className="adm-input"
                          data-testid="target-start-folgopp"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => { setStartAapen(false); onConvert({ ownerId: startAnsvarlig || null, followUp: startFolgOpp || null }); }}
                        className="adm-btn w-full justify-center mt-3.5"
                        data-testid="target-start-bekreft"
                      >
                        <ArrowRightCircle size={13} /> Start outreach
                      </button>
                      <p className="text-[10px] text-neutral-400 mt-2 leading-relaxed">
                        Targetet arkiveres og speakeren opprettes i pipelinen med all kontekst.
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <Seksjon label="Vurdering">
            <Felt label="Hvorfor dette navnet?" verdi={target.rationale} onLagre={(v) => onPatch({ rationale: v })} testid="target-rationale" textarea />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <label className="adm-label">
                Sannsynlighet (%)
                <input
                  type="number"
                  defaultValue={target.probability ?? ""}
                  onBlur={(e) => {
                    const v = e.target.value === "" ? null : Number(e.target.value);
                    if (v !== target.probability) onPatch({ probability: v });
                  }}
                  className="adm-input"
                  data-testid="target-probability"
                />
              </label>
              <label className="adm-label">
                Fee-estimat (NOK)
                <input
                  type="number"
                  defaultValue={target.feeEstimate ?? ""}
                  onBlur={(e) => {
                    const v = e.target.value === "" ? null : Number(e.target.value);
                    if (v !== target.feeEstimate) onPatch({ feeEstimate: v });
                  }}
                  className="adm-input"
                  data-testid="target-fee-estimat"
                />
              </label>
            </div>
          </Seksjon>

          <Seksjon label="Vei inn">
            <div className="grid grid-cols-2 gap-3">
              <Felt label="Kontaktvei" verdi={target.contactRoute} onLagre={(v) => onPatch({ contactRoute: v })} testid="target-kontaktvei" />
              <Felt label="Varm intro via" verdi={target.warmIntro} onLagre={(v) => onPatch({ warmIntro: v })} testid="target-warm-intro" />
            </div>
          </Seksjon>

          <Seksjon label="Neste steg">
            <div className="grid grid-cols-2 gap-3">
              <Felt label="Neste handling" verdi={target.nextAction} onLagre={(v) => onPatch({ nextAction: v })} testid="target-next-action" />
              <Felt label="Dato" verdi={target.nextActionDate} onLagre={(v) => onPatch({ nextActionDate: v })} testid="target-next-dato" type="date" />
              <label className="adm-label col-span-2">
                Intern ansvarlig
                <select
                  value={target.ownerId || ""}
                  onChange={(e) => onPatch({ ownerId: e.target.value || null })}
                  className="adm-input"
                  data-testid="target-owner-select"
                >
                  <option value="">Ingen</option>
                  {(members || []).map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </label>
            </div>
          </Seksjon>

          <Seksjon label="Grunnfelter">
            <div className="grid grid-cols-2 gap-3">
              <Felt label="Rolle/tittel" verdi={target.title} onLagre={(v) => onPatch({ title: v })} testid="target-tittel" />
              <Felt label="Organisasjon" verdi={target.org} onLagre={(v) => onPatch({ org: v })} testid="target-org" />
            </div>
            <div className="mt-3">
              <Felt label="Notater" verdi={target.notes} onLagre={(v) => onPatch({ notes: v })} testid="target-notater" textarea />
            </div>
          </Seksjon>

          {!konvertert && (
            <Seksjon label="Arkiv">
              <button
                type="button"
                onClick={() => onPatch({ archived: !target.archived })}
                className="adm-btn-ghost adm-btn-sm"
                data-testid="target-arkiver-btn"
              >
                <Archive size={12} /> {target.archived ? "Hent tilbake fra arkivet" : "Arkiver targetet"}
              </button>
            </Seksjon>
          )}

          {aktivitet.length > 0 && (
            <Seksjon label="Aktivitet">
              <div className="space-y-1">
                {aktivitet.slice(0, 8).map((a, i) => (
                  <p key={i} className="text-[11px] text-neutral-400 leading-relaxed">
                    <span className="text-neutral-600 font-medium">{a.actor}</span> · {a.text}
                    <span className="ml-1.5 text-neutral-300">{new Date(a.at).toLocaleDateString("nb-NO", { day: "numeric", month: "short" })}</span>
                  </p>
                ))}
              </div>
            </Seksjon>
          )}
        </div>
      </aside>
    </div>
  );
}
